// src/ui/VoiceBubble.jsx — the voice memo inside the conversation.
//
// Renders memos recorded on a phone and on the web identically: the phone ships a
// 35-value `waveform` of pixel bar heights (3-25) plus `duration` in seconds, and this
// draws exactly that. When those are missing — the phone's history export drops them,
// so every synced memo arrives bare — it decodes the audio and computes real peaks
// rather than falling back to a fake wave that looks real but isn't.
import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Mic } from './icons';
import { peaksFromAudio, timeLabel, WAVE_MAX, WAVE_MIN } from '../services/voice';

// module-level so scrolling back to an old memo doesn't re-decode it
const _peakCache = new Map();
const _playedOnce = new Set();

export default function VoiceBubble({ m, mine, hostRef }) {
  const url = m._media;
  const [derived, setDerived] = useState(() => _peakCache.get(m.id) || null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [rate, setRate] = useState(1);
  const [heard, setHeard] = useState(() => _playedOnce.has(m.id));
  const audioRef = useRef(null);
  const waveRef = useRef(null);
  const dragRef = useRef(false);
  const rafRef = useRef(0);

  // 🟥 WHY THE DOT JUMPED. <audio>'s `timeupdate` fires roughly FOUR times a second —
  //    the spec leaves the rate to the browser and nobody fires it per frame. Driving
  //    the playhead from it meant the dot sat still for ~250ms and then lurched, which
  //    no CSS transition can rescue (a transition long enough to bridge the gap just
  //    makes the dot lag the audio instead). While playing we read `currentTime` on
  //    every animation frame instead; `timeupdate` stays as the idle/seek sync.
  useEffect(() => {
    if (!playing) { cancelAnimationFrame(rafRef.current); return undefined; }
    const tick = () => {
      const el = audioRef.current;
      if (el && !dragRef.current) setPos(el.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  const stored = Array.isArray(m.waveform) && m.waveform.length > 0 ? m.waveform : null;
  const wave = stored || derived?.waveform || null;
  const total = (Number.isFinite(m.duration) && m.duration > 0)
    ? m.duration
    : (derived?.durationSec || 0);

  // only decode when the message genuinely lacks its own data
  useEffect(() => {
    let live = true;
    if (!url || stored || derived) return undefined;
    (async () => {
      const out = await peaksFromAudio(url);
      if (!live || !out) return;
      _peakCache.set(m.id, out);
      setDerived(out);
    })();
    return () => { live = false; };
  }, [url, stored, derived, m.id]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      // one memo at a time — starting this one stops whatever else is playing
      document.querySelectorAll('audio[data-voice]').forEach((a) => { if (a !== el) a.pause(); });
      el.playbackRate = rate;
      el.play();
      setPlaying(true);
      if (!heard) { _playedOnce.add(m.id); setHeard(true); }
    } else { el.pause(); setPlaying(false); }
  };

  const cycleRate = (e) => {
    e.stopPropagation();
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  // click OR drag anywhere on the wave to scrub
  const seekTo = (clientX) => {
    const el = audioRef.current;
    const box = waveRef.current;
    if (!el || !box || !total) return;
    const r = box.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    el.currentTime = ratio * total;
    setPos(ratio * total);
  };
  const onDown = (e) => { if (!url) return; dragRef.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); seekTo(e.clientX); };
  const onMove = (e) => { if (dragRef.current) seekTo(e.clientX); };
  const onUp = () => { dragRef.current = false; };

  const played = total ? Math.min(1, pos / total) : 0;
  const bars = wave || new Array(35).fill(WAVE_MIN + 3);
  const showing = playing || pos > 0 ? pos : total;

  return (
    <div className={`vb ${mine ? 'out' : 'in'}`} ref={hostRef}>
      <button className="vb-play" onClick={toggle} disabled={!url} title={playing ? 'Pause' : 'Play'}>
        {!url
          ? <span className="vb-spin" />
          : playing ? <Pause width={18} height={18} /> : <Play width={18} height={18} />}
      </button>

      <div className="vb-mid">
        <div
          className={`vb-wave ${wave ? '' : 'pending'}`}
          ref={waveRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          {bars.map((h, i) => (
            <span
              key={i}
              className={`vb-bar ${(i + 0.5) / bars.length <= played ? 'on' : ''}`}
              style={{ height: `${(Math.max(WAVE_MIN, Math.min(WAVE_MAX, h)) / WAVE_MAX) * 100}%` }}
            />
          ))}
          {url && total > 0 && <span className="vb-thumb" style={{ left: `${played * 100}%` }} />}
        </div>

        <div className="vb-foot">
          <span className="vb-time">{timeLabel(showing)}</span>
          <span className="vb-right">
            {!mine && !heard && url && <span className="vb-new" title="Not played yet" />}
            <Mic width={11} height={11} className="vb-mic" />
            <button className={`vb-rate ${rate !== 1 ? 'on' : ''}`} onClick={cycleRate} title="Playback speed">
              {rate}×
            </button>
          </span>
        </div>
      </div>

      {url && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audioRef}
          data-voice=""
          src={url}
          preload="metadata"
          onTimeUpdate={(e) => { if (!playing && !dragRef.current) setPos(e.currentTarget.currentTime); }}
          onEnded={() => { setPlaying(false); setPos(0); }}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
        />
      )}
    </div>
  );
}
